/* eslint-disable max-lines */
import React from 'react';
import { Prompt } from 'react-router';
import type { StaticContext } from 'react-router';
import _ from 'lodash';
import { withTheme } from 'styled-components';
import type { Moment } from 'moment';
import moment from 'moment';
import { styled } from '@this/constants/themes';
import Text from '@this/components/shared/text/text';

import ContentHeader from '@this/components/shared/content_header/content_header';
import SimpleLoading from '@this/components/shared/simple_loading/simple_loading';
import Modal from '@this/shared/modal/modal';
import Confirm from '@this/shared/confirm/confirm';

import User, { convertUserResponseToArgs, type UserResponseArgs } from '@this/domain/user/user2';
import NonOrderItem from '@this/domain/non_order_item2';
import NonOrderItemPrice from '@this/domain/non_order_item_price2';
import NonOrderItemPriceDetail from '@this/domain/non_order_item_price_detail2';
import TransportElement from '@this/domain/transport_element2';
import TransportElementTerminal from '@this/domain/transport_element_terminal/transport_element_terminal2';
import HotelElement from '@this/domain/hotel_element2';
import SelectRepository from '@this/domain/select_repository';
import SelectStore from '@this/domain/select_store';
import SearchQuery from '@this/domain/search_query';
import type { AvailableRepository } from '@this/domain/available_repository';
import type { RouteComponentProps } from 'react-router-dom';
import Trip, { convertTripResponseToArgs, type TripResponseArgs } from '@this/src/domain/trip/trip2';
import TravelerInformation, { buildTravelerParams } from '@this/domain/traveler_information';
import { Fetcher, HTTPError } from '@this/src/util';
import type OrderItem from '@this/domain/order_item2';
import { reportError } from '@this/lib/bugsnag';
import { Button } from '@this/shared/ui/inputs/button';
import SearchResultItem from '@this/domain/search_result_item';
import { Wrap, BodyWrap, Body, LoadingWrap } from '../trips/trips';
import TripsFormBasicinfoTemplate from './trips_form_block/trips_form_basicinfo.template';
import type { AirlineName } from './trips_form_block/trips_form_itemform.template';
import TripsFormItemformTemplate from './trips_form_block/trips_form_itemform.template';
import {
  areTravelerInformationsEqual,
  cloneNonOrderItemForEditing,
  convertTravelerInformationsToTravelerList,
  convertTravelerListToTravelerInformations,
  createInitialTravelerInformations,
  getNonOrderItemElementType
} from './utils';

const TRIPS_CONFIRM_DATA_KEY = 'tripsConfirmData';
const DEFAULT_TRAVELERS_COUNT = 1;

type AddedItemValue = NonOrderItem | OrderItem | SearchResultItem;

interface Props extends RouteComponentProps<Record<string, string | undefined>, StaticContext, unknown> {
  serviceId: number;
  availableRepos: AvailableRepository[];
  theme: { themeClass: string };
}

interface TripsState {
  serviceId: number | undefined;
  trip: Trip | null;
  user: User | null;
  members: User[];
  travelers: TravelerInformation[];
  travelerBirthdayInputs: string[];
  title: string;
  loading: boolean;
  shouldBlockNavigation: boolean;
  validationErrors: TripFormErrors;
  outdate: Moment;
  homedate: Moment;
  query: SearchQuery | undefined;
  store: SelectStore | undefined;
  repository: SelectRepository | undefined;
  domesticAirlines: AirlineName[];
  internationalAirlines: AirlineName[];
  isSmartSearching: boolean;
  isConfirmingAddTraveler: boolean;
  removingTravelerNumber: number | null;
  showItemForm: boolean;
  selectedItemType: string | null;
  creatingNonOrderItem: NonOrderItem | null;
  editingNonOrderItem: NonOrderItem | null;
  error: string | null;
  showSearchBox: boolean;
  tripItemFormTemplateErrMsg: string;
  shortdistance: boolean;
  useSmartSearch: boolean;
  preventZeroAmountApplication: boolean;
  isTravelerInputComposing: boolean;
}

interface TripsEditResponse {
  trip: TripResponseArgs | null;
  user: UserResponseArgs;
  members: UserResponseArgs[];
  query: Record<string, unknown> | null;
  shortdistance: boolean;
  use_smart_search: boolean;
  prevent_zero_amount_application: boolean;
  default_smart_search_enabled: boolean;
  show_fee: boolean;
}

interface TravelerErrors {
  memberSelect?: string | undefined;
  travelerFirstNameRomen?: string | undefined;
  travelerLastNameRomen?: string | undefined;
  travelerFirstNameKana?: string | undefined;
  travelerLastNameKana?: string | undefined;
  travelerBirthday?: string | undefined;
  travelerGender?: string | undefined;
  [key: string]: string | undefined;
}

interface TripFormErrors {
  travelers: TravelerErrors[];
  hotelDeadline: string | null;
  domesticAirDeadline: string | null;
  zeroAmount: string | null;
  errors: string[];
  [key: string]: unknown;
}

const getItemStartDateTime = (item: AddedItemValue) => {
  if (item instanceof NonOrderItem) return item.startDateTime;
  if (item instanceof SearchResultItem) return item.startDateTime();
  if (item.elements && item.elements.length > 0) {
    const element = item.elements[0];
    if ('checkinDate' in element && element.checkinDate) return element.checkinDate;
    if ('startTime' in element && element.startTime) return element.startTime;
  }
  return null;
};

const getIsForeignFlightCustomerInfoRequired = (trip: Trip | null, store: SelectStore | undefined) => {
  if (trip?.foreignAir) return true;
  const resultItems = store?.result.items || [];
  return resultItems.some(item => item.elementType() === 'flight');
};

const getNonOrderItemParams = (item: NonOrderItem) => item.updateParams;

const buildNonOrderItem = (elementType: string, peopleNum: number, outdate: Moment, homedate: Moment) => {
  const timeZone = 'Asia/Tokyo';
  const from = new TransportElementTerminal({
    time: outdate.clone(),
    timeZone,
    name: ''
  });
  const to = new TransportElementTerminal({
    time: outdate.clone(),
    timeZone,
    name: ''
  });
  const details =
    elementType === 'rental_car'
      ? [new NonOrderItemPriceDetail({ id: 0, price: 0, purpose: 'rental_car_gas' })]
      : [];
  const price = new NonOrderItemPrice({ id: 0, price: 0, details });

  if (elementType === 'hotel') {
    // idをつけて初期化しているが送信しないようにしている
    const hotel = new HotelElement({
      id: 0,
      name: '',
      roomNum: 1,
      checkinDate: outdate.clone(),
      checkoutDate: homedate.clone(),
      provider: '',
      planName: '',
      roomType: '',
      restricted: false,
      tooLate: false
    });
    return new NonOrderItem({
      price,
      peopleNum,
      nonOrderItemMappings: [],
      transports: [],
      hotels: [hotel],
      paymentType: 'cash',
      requestType: 'simple_request',
      isNeed: true,
      supplier: '',
      tripType: 'round_trip',
      elementType
    });
  }

  const transport = new TransportElement({
    transportType: elementType as never,
    from,
    to,
    boardingStation: '',
    arrivalStation: ''
  });
  return new NonOrderItem({
    price,
    peopleNum,
    nonOrderItemMappings: [],
    transports: [transport],
    hotels: [],
    paymentType: 'cash',
    requestType: 'simple_request',
    isNeed: true,
    supplier: '',
    tripType: 'round_trip',
    elementType
  });
};

const isTripEditable = (trip: Trip | null) => {
  if (!trip) return true;
  const draft = Boolean(trip.draft);
  return draft || trip.tripDetailEditable;
};

class TripsForm extends React.Component<Props, TripsState> {
  constructor(props: Props) {
    super(props);
    const lState = props.location.state as { title?: string; outdate?: string; homedate?: string } | undefined;

    this.state = {
      serviceId: props.serviceId,
      trip: null,
      user: null,
      members: [],
      travelers: [],
      travelerBirthdayInputs: [],
      title: utils.dig(lState, 'title') || '',
      loading: true,
      shouldBlockNavigation: true,
      validationErrors: {
        travelers: [],
        hotelDeadline: null,
        domesticAirDeadline: null,
        zeroAmount: null,
        errors: []
      },
      outdate: lState && lState.outdate ? moment(lState.outdate) : moment().add(1, 'days'),
      homedate: lState && lState.homedate ? moment(lState.homedate) : moment().add(2, 'days'),
      query: undefined,
      store: undefined,
      repository: undefined,
      domesticAirlines: [],
      internationalAirlines: [],
      isSmartSearching: false,
      isConfirmingAddTraveler: false,
      removingTravelerNumber: null,
      showItemForm: true,
      selectedItemType: null,
      creatingNonOrderItem: null,
      editingNonOrderItem: null,
      error: null,
      showSearchBox: false,
      tripItemFormTemplateErrMsg: '',
      shortdistance: false,
      useSmartSearch: true,
      preventZeroAmountApplication: false,
      isTravelerInputComposing: false
    };
  }

  componentDidMount() {
    const lState = this.props.location.state as
      | { title?: string; outdate?: string; homedate?: string }
      | undefined;
    // クエリパラメータの制約対策
    Fetcher.post<TripsEditResponse>(`${location.href}.json`, lState || {}).then(
      result => {
        const user = new User(convertUserResponseToArgs(result.user));
        const members = _.map(result.members, m => new User(convertUserResponseToArgs(m)));
        const trip = result.trip
          ? new Trip(convertTripResponseToArgs({ ...result.trip, user: result.user }, result.show_fee ?? false))
          : null;
        const travelerCount = trip?.peoplenum || DEFAULT_TRAVELERS_COUNT;
        const baseTravelers =
          trip && trip.travelerInformations.length > 0
            ? trip.travelerInformations
            : createInitialTravelerInformations(travelerCount, user, members);
        if ((!trip || trip.id === null) && baseTravelers[0]) {
          const selfTraveler = new TravelerInformation({ type: 'self' });
          selfTraveler.assignFromUser(user);
          baseTravelers[0] = selfTraveler;
        }
        const travelers = baseTravelers.map(traveler => {
          if (traveler.type === 'self' && !traveler.user) {
            traveler.user = user;
            traveler.assignFromUser(user);
          }
          if (traveler.type === 'member' && traveler.userId && !traveler.user) {
            const member = members.find(m => m.id === traveler.userId);
            if (member) {
              traveler.user = member;
              traveler.assignFromUser(member);
            }
          }
          return traveler;
        });
        const travelerList = convertTravelerInformationsToTravelerList(travelers, travelers.length, user, members);
        const queryStartTime = utils.dig(result.query, 'departure_time')
          ? moment(utils.dig(result.query, 'departure_time'))
          : trip?.startTime || this.state.outdate;
        const queryEndTime = utils.dig(result.query, 'return_time')
          ? moment(utils.dig(result.query, 'return_time'))
          : trip?.endTime || this.state.homedate;
        const query = result.query
          ? new SearchQuery(
              _.merge({}, result.query, {
                cabin: (user.defaultSeatClasses || []).join(','),
                defaultSeatClasses: user.defaultSeatClasses,
                travelers: travelerList,
                time: {
                  startTime: queryStartTime,
                  endTime: queryEndTime
                },
                availableRepos: this.props.availableRepos
              })
            )
          : new SearchQuery({
              cabin: (user.defaultSeatClasses || []).join(','),
              defaultSeatClasses: user.defaultSeatClasses,
              peoplenum: travelers.length,
              travelers: travelerList,
              time: {
                startTime: trip?.startTime || this.state.outdate,
                endTime: trip?.endTime || this.state.homedate
              },
              availableRepos: this.props.availableRepos
            });
        const tripStartTime = trip?.cacheStartTime || trip?.startTime;
        const tripEndTime = trip?.cacheEndTime || trip?.endTime;
        const outdate =
          (lState && lState.outdate ? moment(lState.outdate) : null) || tripStartTime || this.state.outdate;
        const homedate =
          (lState && lState.homedate ? moment(lState.homedate) : null) || tripEndTime || this.state.homedate;
        this.fetchAirlines();

        const isInitSmartSearchEnabled =
          (trip === null || trip.id === null || trip.draft) &&
          result.use_smart_search &&
          result.default_smart_search_enabled;
        this.setState(
          {
            trip,
            user,
            members,
            travelers,
            travelerBirthdayInputs: travelers.map(t =>
              t.birthday ? moment(t.birthday).format('YYYY-MM-DD') : ''
            ),
            title: trip !== null && trip.id !== null ? trip.title || this.state.title : this.state.title,
            outdate,
            homedate,
            query,
            loading: false,
            // showItemForm: result.query !== null,
            isSmartSearching: isInitSmartSearchEnabled || result.query !== null,
            shortdistance: result.shortdistance,
            useSmartSearch: result.use_smart_search,
            preventZeroAmountApplication: result.prevent_zero_amount_application
          },
          () => {
            if (trip?.draft && result.query && query) {
              this.handleSmartSearch(query);
            }
          }
        );
      },
      () => {
        this.setState({
          error: '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        });
      }
    );
  }

  componentDidUpdate(_prevProps: Props, prevState: TripsState) {
    const { query, user, members, travelers } = this.state;
    if (query && query.travelers && user) {
      const nextTravelers = convertTravelerListToTravelerInformations(
        query.travelers,
        query.peoplenum,
        user,
        members
      );
      const didSyncQueryTravelers =
        prevState.travelers !== travelers && areTravelerInformationsEqual(prevState.travelers, nextTravelers);
      if (!didSyncQueryTravelers && !areTravelerInformationsEqual(travelers, nextTravelers)) {
        this.setState({
          travelers: nextTravelers,
          travelerBirthdayInputs: nextTravelers.map(t => (t.birthday ? t.birthday.format('YYYY-MM-DD') : ''))
        });
        return;
      }
    }

    // 未保存離脱の警告をPromptで実装しているが、再読み込みとタブの終了に対応していないため
    if (this.state.shouldBlockNavigation) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  }

  async fetchAirlines() {
    const domestic = await Fetcher.get<{ airlines: AirlineName[] }>('/domestic_airlines.json');
    const international = await Fetcher.get<{ airlines: AirlineName[] }>('/international_airlines.json');
    this.setState({
      domesticAirlines: domestic.airlines,
      internationalAirlines: international.airlines
    });
  }

  addedItems = () => {
    const nonOrderItems: NonOrderItem[] = this.state.trip?.nonOrderItems || [];
    let items: AddedItemValue[] = [...nonOrderItems];
    if (this.state.trip?.draft) {
      items = items.concat(this.state.store?.result.items || []);
    } else {
      items = items.concat(this.state.trip?.order?.orderItems || []);
    }

    return _.sortBy(items, item => {
      const startDateTime = getItemStartDateTime(item);
      return startDateTime ? startDateTime.valueOf() : 0;
    });
  };

  handleChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ title: e.target.value });
  };

  handleTravelerTypeChange = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const travelerType = value as 'self' | 'member' | 'companion';
    this.setState(
      prev => {
        const travelers = [...prev.travelers];
        const travelerBirthdayInputs = [...prev.travelerBirthdayInputs];
        if (travelerType === 'self' && prev.user) {
          const selfTraveler = new TravelerInformation({ type: 'self' });
          selfTraveler.user = prev.user;
          selfTraveler.assignFromUser(prev.user);
          travelers[i] = selfTraveler;
          travelerBirthdayInputs[i] = '';
        } else if (travelerType === 'companion') {
          travelers[i] = new TravelerInformation({ type: 'companion' });
          travelerBirthdayInputs[i] = '';
        } else if (travelerType === 'member') {
          travelers[i] = new TravelerInformation({ type: 'member' });
          travelerBirthdayInputs[i] = '';
        } else {
          const traveler = travelers[i] || new TravelerInformation({});
          traveler.type = travelerType;
          travelers[i] = traveler;
        }
        return { travelers, travelerBirthdayInputs } as Pick<TripsState, 'travelers' | 'travelerBirthdayInputs'>;
      },
      () => this.setTripTravelersToQuery()
    );
  };

  handleTravelerSelect = (i: number, traveler: TravelerInformation) => {
    this.setState(
      prev => {
        const travelers = [...prev.travelers];
        const travelerBirthdayInputs = [...prev.travelerBirthdayInputs];
        travelers[i] = traveler;
        travelerBirthdayInputs[i] = traveler.birthday ? traveler.birthday.format('YYYY-MM-DD') : '';
        return { travelers, travelerBirthdayInputs } as Pick<TripsState, 'travelers' | 'travelerBirthdayInputs'>;
      },
      () => this.setTripTravelersToQuery()
    );
  };

  handleTravelerInfoChange = (i: number, method: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    this.setState(
      prev => {
        const travelers = [...prev.travelers];
        const travelerBirthdayInputs = [...prev.travelerBirthdayInputs];
        const traveler = travelers[i];
        if (!traveler) return prev;
        switch (method) {
          case 'setLastNameRoman':
            traveler.lastNameRoman = value;
            break;
          case 'setFirstNameRoman':
            traveler.firstNameRoman = value;
            break;
          case 'setLastNameKana':
            traveler.lastNameKana = value;
            break;
          case 'setFirstNameKana':
            traveler.firstNameKana = value;
            break;
          case 'setBirthday':
            travelerBirthdayInputs[i] = value;
            traveler.birthday = moment(value, 'YYYY-MM-DD', true).isValid()
              ? moment(value, 'YYYY-MM-DD')
              : undefined;
            break;
          case 'setFlightGender':
            traveler.flightGender = value as 'male' | 'female';
            break;
          default:
            break;
        }
        travelers[i] = traveler;
        if (prev.query && prev.user) {
          const travelerList = convertTravelerInformationsToTravelerList(
            travelers,
            travelers.length,
            prev.user,
            prev.members
          );
          prev.query.setTravelers(travelerList);
          prev.query.setPeoplenum(travelers.length);
        }
        return {
          travelers,
          travelerBirthdayInputs
        } as Pick<TripsState, 'travelers' | 'travelerBirthdayInputs'>;
      },
      () => {
        if (!this.state.isTravelerInputComposing) {
          this.syncTripTravelersToQuery();
        }
      }
    );
  };

  handleTravelerCompositionStart = () => {
    this.setState({ isTravelerInputComposing: true });
  };

  handleTravelerCompositionEnd = () => {
    this.setState({ isTravelerInputComposing: false }, () => {
      this.syncTripTravelersToQuery();
    });
  };

  setTripTravelersToQuery = () => {
    if (!this.state.query || !this.state.user) {
      return;
    }

    const travelerList = convertTravelerInformationsToTravelerList(
      this.state.travelers,
      this.state.travelers.length,
      this.state.user,
      this.state.members
    );
    const query = this.state.query;
    query.setTravelers(travelerList);
    query.setPeoplenum(this.state.travelers.length);
    this.setState({ query });
  };

  syncTripTravelersToQuery = () => {
    if (!this.state.query || !this.state.user) {
      return;
    }

    const travelerList = convertTravelerInformationsToTravelerList(
      this.state.travelers,
      this.state.travelers.length,
      this.state.user,
      this.state.members
    );
    this.state.query.setTravelers(travelerList);
    this.state.query.setPeoplenum(this.state.travelers.length);
  };

  handleCancelAddTraveler = () => {
    this.setState({ isConfirmingAddTraveler: false });
  };

  handleCancelRemoveTraveler = () => {
    this.setState({ removingTravelerNumber: null });
  };

  handleAddTraveler = (e?: React.MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault();
    }

    if (utils.dig(this.state.store, 'result', 'items') && !this.state.isConfirmingAddTraveler) {
      this.setState({
        isConfirmingAddTraveler: true
      });
    } else {
      this.setState(
        prev => {
          if (!prev.user) return prev;
          const travelers = [...prev.travelers];
          const travelerBirthdayInputs = [...prev.travelerBirthdayInputs];
          const newTraveler = new TravelerInformation({ type: 'self' });
          newTraveler.user = prev.user;
          newTraveler.assignFromUser(prev.user);
          travelers.push(newTraveler);
          travelerBirthdayInputs.push('');
          return {
            travelers,
            travelerBirthdayInputs,
            isConfirmingAddTraveler: false
          } as Pick<TripsState, 'travelers' | 'travelerBirthdayInputs' | 'isConfirmingAddTraveler'>;
        },
        () => {
          this.setTripTravelersToQuery();
          if (this.state.store && this.state.query) {
            this.handleSmartSearch(this.state.query);
          }
        }
      );
    }
  };

  handleRemoveTraveler = (i?: number, e?: React.MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault();
    }

    if (utils.dig(this.state.store, 'result', 'items') && i && !this.state.removingTravelerNumber) {
      this.setState({
        removingTravelerNumber: i
      });
    } else {
      this.setState(
        prev => {
          const index = i ?? prev.removingTravelerNumber;
          if (index === null || index === undefined) return prev;
          const travelers = [...prev.travelers];
          const travelerBirthdayInputs = [...prev.travelerBirthdayInputs];
          travelers.splice(index, 1);
          travelerBirthdayInputs.splice(index, 1);
          return {
            travelers,
            travelerBirthdayInputs,
            removingTravelerNumber: null
          } as Pick<TripsState, 'travelers' | 'travelerBirthdayInputs' | 'removingTravelerNumber'>;
        },
        () => {
          this.setTripTravelersToQuery();
          if (this.state.store && this.state.query) {
            this.handleSmartSearch(this.state.query);
          }
        }
      );
    }
  };

  handleChangeDate = (kind: 'outdate' | 'homedate', date: Moment) => {
    let outMoment = this.state.outdate;
    let homeMoment = this.state.homedate;

    switch (kind) {
      case 'outdate': {
        outMoment = date;
        if (date > homeMoment) {
          homeMoment = moment(outMoment);
        }
        break;
      }
      case 'homedate': {
        homeMoment = date;
        if (date < outMoment) {
          outMoment = moment(homeMoment);
        }
        break;
      }
      default: {
        break;
      }
    }
    const query = this.state.query;
    if (!this.state.repository && query) {
      query.resetTime(outMoment, homeMoment);
    }

    this.setState({
      query,
      outdate: outMoment,
      homedate: homeMoment
    });
  };

  handleShowItemForm = () => {
    this.setState({
      showItemForm: true,
      isSmartSearching: false,
      selectedItemType: null,
      creatingNonOrderItem: null,
      editingNonOrderItem: null
    });
  };

  changeSmartSearch = () => {
    const flag = this.state.isSmartSearching;
    this.setState({ isSmartSearching: !flag });
    app.render();
  };

  handleSmartSearch = (query: SearchQuery) => {
    Fetcher.post('/search_histories', query.searchHistoryParams());
    this.setState(
      {
        shouldBlockNavigation: false
      },
      () => {
        if (this.state.query) {
          this.props.history.push(`${location.pathname}${query.getQueryString()}`);

          const store = new SelectStore({ query: this.state.query }, this.state.user, this.props.availableRepos);

          this.setState(
            {
              store,
              repository: new SelectRepository({ store })
            },
            () => {
              if (this.state.repository) {
                this.state.repository.startSearch(this.props.theme.themeClass);
              }
            }
          );
        }

        this.setState({
          shouldBlockNavigation: true
        });
      }
    );
  };

  selectItemType = (elementType: string) => {
    if (this.state.trip) {
      const item = buildNonOrderItem(
        elementType,
        this.state.travelers.length,
        this.state.outdate,
        this.state.homedate
      );
      this.setState({
        selectedItemType: elementType,
        creatingNonOrderItem: item
      });
    }
  };

  deselectItemType = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    this.setState({
      selectedItemType: null,
      creatingNonOrderItem: null,
      editingNonOrderItem: null
    });
  };

  addNonOrderItemToTrip = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!this.state.creatingNonOrderItem) {
      return;
    }
    const trip = this.state.trip;
    if (!trip) return;

    const totalPrice =
      this.state.creatingNonOrderItem.price.totalPrice * this.state.creatingNonOrderItem.peopleNum;
    if (isNaN(totalPrice)) {
      this.setState({ tripItemFormTemplateErrMsg: '金額を入力してください' });
      return;
    }
    if (totalPrice === 0 && this.state.preventZeroAmountApplication) {
      this.setState({ tripItemFormTemplateErrMsg: '金額は1円以上で入力してください' });
      return;
    }

    if (this.state.editingNonOrderItem) {
      const nonOrderItems = trip.nonOrderItems as NonOrderItem[];
      trip.nonOrderItems = _.filter(
        nonOrderItems,
        item => item !== this.state.editingNonOrderItem
      ) as NonOrderItem[];
    }
    (trip.nonOrderItems as NonOrderItem[]).push(this.state.creatingNonOrderItem);
    this.setState({
      trip,
      showItemForm: false,
      selectedItemType: null,
      creatingNonOrderItem: null,
      tripItemFormTemplateErrMsg: ''
    });
  };

  static handleTimeChange(item: NonOrderItem, type: 'from' | 'to', time: Moment) {
    item.transports[0][type].time = time;
    app.render();
  }

  startEditingNonOrderItem = (item: NonOrderItem) => {
    this.setState({
      showItemForm: true,
      isSmartSearching: false,
      selectedItemType: getNonOrderItemElementType(item),
      creatingNonOrderItem: cloneNonOrderItemForEditing(item),
      editingNonOrderItem: item
    });
  };

  toggleNonOrderItemNeed = (item: NonOrderItem) => {
    item.isNeed = !item.isNeed;
    app.render();
    if (this.state.editingNonOrderItem === item) {
      this.setState({
        selectedItemType: null,
        creatingNonOrderItem: null,
        editingNonOrderItem: null
      });
    }
  };

  handleSelectItem = () => {
    this.updateUrl();
  };

  private updateUrl() {
    this.setState(
      {
        shouldBlockNavigation: false
      },
      () => {
        if (this.state.store && this.state.query) {
          let href = location.pathname;
          href += this.state.query.getQueryString();
          if (this.state.store.result) {
            href += this.state.store.result.getQueryString();
          }
          this.props.history.push(href);
          const params = utils.getParams();
          _.map(params.items, (item, i) => {
            if (item.item_type === 'hotel') {
              _.merge(item, {
                prefecture: this.state.repository ? this.state.repository.getPrefecture(Number(i)) : ''
              });
            }
            return item;
          });
        }

        this.setState({
          shouldBlockNavigation: true
        });
      }
    );
  }

  handleSubmit = async (e: React.MouseEvent<HTMLElement>) => {
    e.persist();
    const validationErrors = this.validationErrors();
    await this.validate(validationErrors);
    if (_.isEmpty(validationErrors)) {
      this.submit();
    } else {
      e.preventDefault();
      this.setState({ validationErrors });
    }
  };

  validZeroAmount = () => {
    if (!this.state.preventZeroAmountApplication) {
      return true;
    }
    // 0円の旅程登録チェック
    const nonOrderItems: NonOrderItem[] = this.state.trip?.nonOrderItems || [];
    const neededNonOrderItems = nonOrderItems.filter(item => item.isNeed);
    if (neededNonOrderItems.some(item => item.price.price === 0)) {
      return false;
    }

    if (this.state.trip?.draft) {
      const resultItems = this.state.store?.result.items || [];
      const neededResultItems = resultItems.filter(item => item.isNeed);
      return neededNonOrderItems.length > 0 || neededResultItems.length > 0;
    }

    const orderItems: OrderItem[] = this.state.trip?.order?.orderItems || [];
    return neededNonOrderItems.length > 0 || orderItems.length > 0;
  };

  async validate(errors: TripFormErrors) {
    try {
      const params = {
        query: this.state.store && this.state.query ? this.state.query.getSubmitParams() : {},
        search_result: this.state.store ? this.state.store.result.confirmParams() : {}
      };
      await Fetcher.post('/trips/confirm/validate', params);
    } catch (e) {
      if (e instanceof HTTPError && e.response?.status === 400) {
        const deadline = e.response.data.hotel_deadline;
        errors.hotelDeadline = deadline;
        const airDeadline = e.response.data.domestic_air_deadline;
        errors.domesticAirDeadline = airDeadline;

        const res_err = e.response.data.errors;
        errors.errors = res_err || [];
      }
    }
    if (this.state.store && this.state.query && !this.state.useSmartSearch) {
      const resultErrors = this.state.store.result.errors(this.state.query);
      const err = (errors.errors || []).concat(resultErrors || []);
      if (!_.isEmpty(err)) errors.errors = err;
    }
  }

  submit() {
    if (this.state.trip?.draft) {
      const href = this.state.trip.id ? `/trips/${this.state.trip.id}/confirm` : '/trips/confirm';
      const params = this.submitParams();

      sessionStorage.setItem(TRIPS_CONFIRM_DATA_KEY, JSON.stringify(params));

      this.setState(
        {
          shouldBlockNavigation: false
        },
        () => {
          this.props.history.push({ pathname: href, state: params });
        }
      );
    } else if (this.state.trip) {
      Fetcher.put(`/trips/${this.state.trip.id}/confirm`, this.submitParams()).then(
        () => {
          this.setState(
            {
              shouldBlockNavigation: false
            },
            () => {
              this.props.history.push('/trips');
            }
          );
        },
        () => {
          this.setState({
            error: '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
          });
        }
      );
    }
  }

  submitParams() {
    const isForeignFlightCustomerInfoRequired = getIsForeignFlightCustomerInfoRequired(
      this.state.trip,
      this.state.store
    );
    return {
      params: {
        title: this.state.title,
        travelers: buildTravelerParams(this.state.travelers, isForeignFlightCustomerInfoRequired),
        departure_time: this.state.outdate.format('YYYY-MM-DD HH:mm ZZ'),
        return_time: this.state.homedate.format('YYYY-MM-DD HH:mm ZZ'),
        non_order_items: _.compact(
          _.map(this.state.trip?.nonOrderItems || [], item => getNonOrderItemParams(item))
        ),
        search_result: this.state.store ? this.state.store.result.confirmParams() : {}
      },
      query: this.state.store && this.state.query ? this.state.query.getSubmitParams() : {}
    };
  }

  validationErrors() {
    const isForeignFlightCustomerInfoRequired = getIsForeignFlightCustomerInfoRequired(
      this.state.trip,
      this.state.store
    );
    const errors: TripFormErrors = {
      travelers: this.state.travelers.map(t => {
        const tError: TravelerErrors = {};
        if (utils.dig(this.state.user, 'organization')) {
          t.validate(tError, isForeignFlightCustomerInfoRequired);
          if (t.type === 'companion') {
            tError.travelerBirthday = TripsForm.dateError(
              '出張者の誕生日',
              t.birthday ? t.birthday.format('YYYY-MM-DD') : undefined
            );
            tError.travelerGender = TripsForm.requiredError('出張者の性別', t.flightGender);
          }
        }
        return tError;
      }),
      hotelDeadline: null,
      domesticAirDeadline: null,
      zeroAmount: null,
      errors: []
    };

    errors.travelers = _.map(errors.travelers, te => utils.compactObject(te));
    errors.travelers = utils.compactObject(errors.travelers);
    errors.zeroAmount = this.validZeroAmount()
      ? null
      : '0円での申請は設定で禁止されています。旅程内容と金額を1つ以上入力してください。';
    return utils.compactObject(errors);
  }

  toggleShowSearchBox = () => {
    this.setState({ showSearchBox: !this.state.showSearchBox });
  };

  handleOpenShowSearchBox = () => {
    this.setState({ showSearchBox: true });
  };

  static requiredError(name: string, value: string | undefined, message = 'を入力してください') {
    if (!value || value.length === 0) {
      return name + message;
    }
    return undefined;
  }

  static katakanaError(name: string, value: string | undefined) {
    const error = TripsForm.requiredError(name, value);
    if (error) {
      return error;
    }
    if (value && !value.match(/^[ァ-ヶー　]*$/)) {
      return `${name}はカタカナで入力してください`;
    }
    return undefined;
  }

  static alphabetError(name: string, value: string | undefined) {
    const error = TripsForm.requiredError(name, value);
    if (error) {
      return error;
    }
    if (value && !value.match(/^[a-zA-Z]+$/)) {
      return `${name}はアルファベッドで入力してください`;
    }
    return undefined;
  }

  static dateError(name: string, value: string | undefined) {
    const error = TripsForm.requiredError(name, value);
    if (error) {
      return error;
    }
    if (!value || !moment(value).isValid()) {
      return `${name}を正しく入力してください（例：1986-01-01）`;
    }
    return undefined;
  }

  render() {
    const {
      serviceId,
      trip,
      user,
      members,
      travelers,
      title,
      loading,
      shouldBlockNavigation,
      validationErrors,
      outdate,
      homedate,
      query,
      store,
      repository,
      domesticAirlines,
      internationalAirlines,
      isSmartSearching,
      isConfirmingAddTraveler,
      removingTravelerNumber,
      showItemForm,
      selectedItemType,
      creatingNonOrderItem,
      editingNonOrderItem,
      error,
      showSearchBox,
      tripItemFormTemplateErrMsg,
      shortdistance,
      useSmartSearch
    } = this.state;
    try {
      return (
        <Wrap data-testid="trips-form-root">
          <Prompt
            when={shouldBlockNavigation}
            message="保存されていないデータが失われます。本当によろしいですか？"
          />
          <FormBodyWrap>
            <div>
              <FormBody>
                <ContentHeader
                  title={`旅程情報${location.pathname === '/trips/new' ? '登録' : '編集'}`}
                  backButton
                  backLink="/trips"
                />
                <TripForm data-testid="trips-form-layout">
                  <TripFormLeft data-testid="trips-form-left">
                    {loading ? (
                      <LoadingWrap data-testid="trips-form-loading">
                        <SimpleLoading />
                      </LoadingWrap>
                    ) : error ? (
                      <LoadingWrap data-testid="trips-form-error">
                        <Error>{error}</Error>
                      </LoadingWrap>
                    ) : user ? (
                      <TripsFormBasicinfoTemplate
                        editable={!!(trip === null || trip.id === null || trip.draft)}
                        travelers={travelers}
                        travelerBirthdayInputs={this.state.travelerBirthdayInputs}
                        user={user}
                        members={members}
                        outdate={trip?.cacheStartTime ?? outdate}
                        homedate={trip?.cacheEndTime ?? homedate}
                        title={title}
                        validationErrors={validationErrors}
                        handleChangeTitle={this.handleChangeTitle}
                        handleRemoveTraveler={this.handleRemoveTraveler}
                        handleTravelerTypeChange={this.handleTravelerTypeChange}
                        handleTravelerSelect={this.handleTravelerSelect}
                        handleTravelerInfoChange={this.handleTravelerInfoChange}
                        handleTravelerCompositionStart={this.handleTravelerCompositionStart}
                        handleTravelerCompositionEnd={this.handleTravelerCompositionEnd}
                        handleAddTraveler={this.handleAddTraveler}
                        handleChangeDate={this.handleChangeDate}
                      />
                    ) : (
                      <LoadingWrap>
                        <SimpleLoading />
                      </LoadingWrap>
                    )}
                  </TripFormLeft>
                  <TripFormRight data-testid="trips-form-right">
                    <Section>
                      <SectionIn data-testid="trips-form-submit-panel">
                        {isTripEditable(trip) && (
                          <Button
                            data-testid="trips-form-submit-top"
                            onClick={e => this.handleSubmit(e)}
                            style={{ width: '200px' }}
                          >
                            {trip === null || trip.draft ? '次へ' : '保存'}
                          </Button>
                        )}
                        {!_.isEmpty(validationErrors.hotelDeadline) && (
                          <Error data-testid="trips-form-validation-hotel-deadline">
                            <Text text={validationErrors.hotelDeadline} />
                          </Error>
                        )}
                        {!_.isEmpty(validationErrors.domesticAirDeadline) && (
                          <Error data-testid="trips-form-validation-domestic-air-deadline">
                            <Text text={validationErrors.domesticAirDeadline} />
                          </Error>
                        )}
                        {!_.isEmpty(validationErrors.zeroAmount) && (
                          <Error data-testid="trips-form-validation-zero-amount">
                            <Text text={validationErrors.zeroAmount} />
                          </Error>
                        )}
                        {!_.isEmpty(validationErrors.errors) && (
                          <Error data-testid="trips-form-validation-errors">
                            <Text text={validationErrors.errors.join('\n')} />
                          </Error>
                        )}
                      </SectionIn>
                    </Section>
                  </TripFormRight>
                </TripForm>
                <TripsFormItemformTemplate
                  serviceId={serviceId}
                  user={user}
                  trip={trip}
                  query={query}
                  store={store}
                  repository={repository}
                  domesticAirlines={domesticAirlines}
                  internationalAirlines={internationalAirlines}
                  addedItems={this.addedItems()}
                  editingNonOrderItem={editingNonOrderItem}
                  creatingNonOrderItem={creatingNonOrderItem}
                  selectedItemType={selectedItemType}
                  selectItemType={this.selectItemType}
                  deselectItemType={this.deselectItemType}
                  startEditingNonOrderItem={this.startEditingNonOrderItem}
                  toggleNonOrderItemNeed={this.toggleNonOrderItemNeed}
                  handleSelectItem={this.handleSelectItem}
                  handleOpenShowSearchBox={this.handleOpenShowSearchBox}
                  handleShowItemForm={this.handleShowItemForm}
                  toggleShowSearchBox={this.toggleShowSearchBox}
                  changeSmartSearch={this.changeSmartSearch}
                  handleSmartSearch={this.handleSmartSearch}
                  addNonOrderItemToTrip={this.addNonOrderItemToTrip}
                  handleTimeChange={TripsForm.handleTimeChange}
                  tripItemFormTemplateErrMsg={tripItemFormTemplateErrMsg}
                  isSmartSearching={isSmartSearching}
                  showItemForm={showItemForm}
                  showSearchBox={showSearchBox}
                  shortdistance={shortdistance}
                  useSmartSearch={
                    !this.state.loading &&
                    (trip === null || trip.id === null || trip.draft ? useSmartSearch : false)
                  }
                />
              </FormBody>
            </div>
            {isConfirmingAddTraveler && (
              <Modal show hideModal={() => this.handleCancelAddTraveler()}>
                <Confirm
                  onConfirmed={() => this.handleAddTraveler()}
                  onAborted={() => this.handleCancelAddTraveler()}
                  message="出張者の人数を追加する場合は、Smart Search の検索結果を追加後の人数で更新します。よろしいですか？"
                />
              </Modal>
            )}
            {removingTravelerNumber && (
              <Modal show hideModal={() => this.handleCancelRemoveTraveler()}>
                <Confirm
                  onConfirmed={() => this.handleRemoveTraveler()}
                  onAborted={() => this.handleCancelRemoveTraveler()}
                  message="出張者の人数を削除する場合は、Smart Search の検索結果を削除後の人数で更新します。よろしいですか？"
                />
              </Modal>
            )}
            <FooterActionGroup>
              <FooterButtonArea data-testid="trips-form-footer">
                {isTripEditable(trip) && (
                  <Button
                    data-testid="trips-form-submit-bottom"
                    onClick={e => this.handleSubmit(e)}
                    style={{ width: '200px' }}
                  >
                    {trip === null || trip.draft ? '次へ' : '保存'}
                  </Button>
                )}
              </FooterButtonArea>
            </FooterActionGroup>
          </FormBodyWrap>
        </Wrap>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

const FormBodyWrap = styled(BodyWrap)`
  padding: 0;

  @media screen and (min-width: 1030px) {
    padding: 20px 0;
  }
`;

const FormBody = styled(Body)`
  padding: 0;
`;

const TripForm = styled.div`
  padding: 20px;

  @media screen and (min-width: 815px) {
    display: flex;
  }

  @media screen and (max-width: 900px) {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const TripFormLeft = styled.div`
  @media screen and (min-width: 815px) {
    width: 80%;
  }
`;

const TripFormRight = styled.div`
  margin: 0 0 0 auto;

  @media screen and (max-width: 814px) {
    display: none;
  }
`;

const Section = styled.div`
  padding-bottom: 20px;
`;

const SectionIn = styled.div`
  margin-left: 10px;
  font-size: 13px;
`;

const Error = styled.div`
  color: ${props => props.theme.redColor};
`;

const FooterActionGroup = styled.div`
  background: #fff;
  position: sticky;
  bottom: 0;
  box-shadow: 0 -3px 3px 3px rgba(0, 0, 0, 0.1);
`;

const FooterButtonArea = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px;
`;

export default withTheme(TripsForm);
